import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../api';

const SECTS = [
    { key: 'all',       label: 'All' },
    { key: 'theravada', label: 'Theravada' },
    { key: 'mahayana',  label: 'Mahayana' },
    { key: 'vajrayana', label: 'Vajrayana' },
    { key: 'zen',       label: 'Zen' },
    { key: 'all-sects', label: 'All Sects' },
];

const SECT_COLORS = {
    theravada:   'bg-green-100 text-green-800',
    mahayana:    'bg-blue-100 text-blue-800',
    vajrayana:   'bg-purple-100 text-purple-800',
    zen:         'bg-stone-100 text-stone-700',
    'all-sects': 'bg-amber-100 text-amber-800',
};

const DIFFICULTY_COLORS = {
    Beginner:     'bg-green-100 text-green-800',
    Intermediate: 'bg-yellow-100 text-yellow-800',
    Advanced:     'bg-red-100 text-red-800',
};

const SectBadge = ({ sect }) => (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${SECT_COLORS[sect] || 'bg-gray-100 text-gray-700'}`}>
        {sect}
    </span>
);

// Deterministic daily pick — same mantra for all users, changes at UTC midnight
const getTodayMantra = (mantras) => {
    if (!mantras.length) return null;
    const daysSinceEpoch = Math.floor(Date.now() / 86400000);
    const idx = ((daysSinceEpoch * 1664525 + 1013904223) >>> 0) % mantras.length;
    return mantras[idx];
};

const FeaturedBanner = ({ mantra, onOpen }) => (
    <button
        onClick={onOpen}
        className="w-full text-left bg-gradient-to-r from-amber-50 to-orange-100 border border-amber-200 rounded-lg px-5 py-4 mb-8 flex items-center gap-4 hover:shadow-md transition-shadow group"
    >
        <span className="text-2xl select-none">☸</span>
        <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-widest mb-0.5">Today's Mantra</p>
            <h2 className="text-lg font-bold text-gray-800 leading-snug">{mantra.title}</h2>
            {mantra.mantra_text_sanskrit && (
                <p className="text-sm text-amber-700 mt-0.5">{mantra.mantra_text_sanskrit}</p>
            )}
        </div>
        <span className="text-amber-400 text-lg group-hover:translate-x-1 transition-transform">→</span>
    </button>
);

const MantraModal = ({ mantra, onClose }) => (
    <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
        onClick={onClose}
    >
        <div
            className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={e => e.stopPropagation()}
        >
            <button
                onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl leading-none"
            >
                ✕
            </button>

            <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">{mantra.title}</h2>
            {mantra.sanskrit_title && (
                <h3 className="text-xl text-center text-amber-700 mb-4 font-semibold">{mantra.sanskrit_title}</h3>
            )}

            <div className="bg-amber-50 rounded-lg p-5 text-center mb-6">
                {mantra.mantra_text_sanskrit && (
                    <p className="text-2xl text-amber-800 font-bold mb-2">{mantra.mantra_text_sanskrit}</p>
                )}
                {mantra.mantra_text && mantra.mantra_text !== mantra.title && (
                    <p className="text-lg text-gray-700 mb-2">{mantra.mantra_text}</p>
                )}
                {mantra.pronunciation_guide && (
                    <p className="text-sm text-gray-500 italic">Pronunciation: {mantra.pronunciation_guide}</p>
                )}
            </div>

            <div className="grid md:grid-cols-2 gap-5 text-sm">
                {mantra.translation && (
                    <div>
                        <h4 className="font-bold text-gray-700 mb-1">Translation</h4>
                        <p className="text-gray-600">{mantra.translation}</p>
                    </div>
                )}
                {mantra.meaning && (
                    <div>
                        <h4 className="font-bold text-gray-700 mb-1">Meaning</h4>
                        <p className="text-gray-600">{mantra.meaning}</p>
                    </div>
                )}
                {mantra.tradition && (
                    <div>
                        <h4 className="font-bold text-gray-700 mb-1">Tradition</h4>
                        <p className="text-gray-600">{mantra.tradition}</p>
                    </div>
                )}
                {mantra.deity_association && (
                    <div>
                        <h4 className="font-bold text-gray-700 mb-1">Deity</h4>
                        <p className="text-gray-600">{mantra.deity_association}</p>
                    </div>
                )}
                {mantra.sects?.length > 0 && (
                    <div>
                        <h4 className="font-bold text-gray-700 mb-1">Sects</h4>
                        <div className="flex flex-wrap gap-1">
                            {mantra.sects.map(s => <SectBadge key={s} sect={s} />)}
                        </div>
                    </div>
                )}
                {mantra.difficulty_level && (
                    <div>
                        <h4 className="font-bold text-gray-700 mb-1">Difficulty</h4>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${DIFFICULTY_COLORS[mantra.difficulty_level] || 'bg-gray-100 text-gray-700'}`}>
                            {mantra.difficulty_level}
                        </span>
                    </div>
                )}
                {mantra.recommended_repetitions && (
                    <div>
                        <h4 className="font-bold text-gray-700 mb-1">Repetitions</h4>
                        <p className="text-gray-600">{mantra.recommended_repetitions}</p>
                    </div>
                )}
            </div>

            {mantra.practice_instructions && (
                <div className="mt-5 bg-gray-50 rounded-lg p-4">
                    <h4 className="font-bold text-gray-700 mb-2">Practice Instructions</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{mantra.practice_instructions}</p>
                </div>
            )}
        </div>
    </div>
);

const MantraCard = ({ mantra, expanded, onToggle }) => (
    <div
        className={`bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transition-shadow hover:shadow-lg ${expanded ? 'ring-2 ring-amber-500' : ''}`}
        onClick={onToggle}
    >
        <div className="p-5">
            <h3 className="text-lg font-bold text-gray-800 mb-1">{mantra.title}</h3>
            {mantra.sanskrit_title && (
                <p className="text-amber-700 font-medium mb-2 text-sm">{mantra.sanskrit_title}</p>
            )}

            {mantra.sects?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                    {mantra.sects.map(s => <SectBadge key={s} sect={s} />)}
                </div>
            )}

            {mantra.pronunciation_guide && (
                <p className="text-xs text-gray-500 italic mb-2">{mantra.pronunciation_guide}</p>
            )}

            <div className="flex items-center gap-2 mt-2">
                {mantra.difficulty_level && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${DIFFICULTY_COLORS[mantra.difficulty_level] || 'bg-gray-100 text-gray-700'}`}>
                        {mantra.difficulty_level}
                    </span>
                )}
                <span className="ml-auto text-xs text-amber-600">{expanded ? 'Close ▲' : 'Expand ▼'}</span>
            </div>
        </div>

        {expanded && (
            <div className="border-t border-amber-100 p-5 bg-amber-50 space-y-3">
                {mantra.mantra_text && (
                    <p className="text-center text-lg font-semibold text-gray-800">{mantra.mantra_text}</p>
                )}
                {mantra.mantra_text_sanskrit && (
                    <p className="text-center text-amber-700">{mantra.mantra_text_sanskrit}</p>
                )}
                {mantra.translation && (
                    <div>
                        <span className="font-semibold text-gray-700 text-sm">Translation: </span>
                        <span className="text-gray-600 text-sm">{mantra.translation}</span>
                    </div>
                )}
                {mantra.meaning && (
                    <div>
                        <span className="font-semibold text-gray-700 text-sm">Meaning: </span>
                        <span className="text-gray-600 text-sm">{mantra.meaning}</span>
                    </div>
                )}
                {mantra.practice_instructions && (
                    <div>
                        <span className="font-semibold text-gray-700 text-sm">Practice: </span>
                        <span className="text-gray-600 text-sm">{mantra.practice_instructions}</span>
                    </div>
                )}
                {mantra.deity_association && (
                    <div>
                        <span className="font-semibold text-gray-700 text-sm">Deity: </span>
                        <span className="text-gray-600 text-sm">{mantra.deity_association}</span>
                    </div>
                )}
                {mantra.recommended_repetitions && (
                    <div>
                        <span className="font-semibold text-gray-700 text-sm">Repetitions: </span>
                        <span className="text-gray-600 text-sm">{mantra.recommended_repetitions}</span>
                    </div>
                )}
            </div>
        )}
    </div>
);

const Mantras = () => {
    const [allMantras, setAllMantras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSect, setActiveSect] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [modalMantra, setModalMantra] = useState(null);

    useEffect(() => {
        document.body.style.backgroundImage = 'url("/images/library.jpg")';
        return () => { document.body.style.backgroundImage = 'none'; };
    }, []);

    useEffect(() => {
        const fetchMantras = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${API_URL}/mantras?limit=100`);
                setAllMantras(res.data?.mantras || []);
            } catch (err) {
                console.error('Error fetching mantras:', err);
                setError('Failed to load mantras. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchMantras();
    }, []);

    const todayMantra = getTodayMantra(allMantras);
    const todayUuid = todayMantra?.uuid;

    const filteredMantras = allMantras
        .filter(m => m.uuid !== todayUuid)
        .filter(m => activeSect === 'all' || m.sects?.includes(activeSect))
        .filter(m => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            return (
                m.title?.toLowerCase().includes(q) ||
                m.mantra_text?.toLowerCase().includes(q) ||
                m.mantra_text_sanskrit?.toLowerCase().includes(q)
            );
        });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-white bg-opacity-90 rounded-lg shadow-lg p-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading mantras...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-white bg-opacity-90 rounded-lg shadow-lg p-8 max-w-2xl w-full mx-4">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <Link to="/library" className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg transition-colors">
                            Back to Library
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8">
            <div className="bg-white bg-opacity-95 rounded-lg shadow-lg p-8 max-w-5xl w-full mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <Link to="/library" className="inline-flex items-center text-amber-600 hover:text-amber-700 mb-4 transition-colors">
                        ← Back to Library
                    </Link>
                    <h1 className="text-4xl font-bold text-gray-800 mb-2">Mantras</h1>
                    <p className="text-gray-600">Sacred sounds for meditation and spiritual practice</p>
                </div>

                {/* Today's Mantra — compact clickable banner */}
                {todayMantra && (
                    <FeaturedBanner
                        mantra={todayMantra}
                        onOpen={() => setModalMantra(todayMantra)}
                    />
                )}

                {/* Browse section */}
                <div>
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="text-2xl font-bold text-gray-800">Browse All Mantras</h2>
                        <button
                            onClick={() => {
                                if (!allMantras.length) return;
                                const pick = allMantras[Math.floor(Math.random() * allMantras.length)];
                                setModalMantra(pick);
                            }}
                            className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                        >
                            Random Mantra
                        </button>
                    </div>

                    {/* Search */}
                    <input
                        type="text"
                        placeholder="Search mantras..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-5 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />

                    {/* Sect filter tabs */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        {SECTS.map(({ key, label }) => (
                            <button
                                key={key}
                                onClick={() => setActiveSect(key)}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                    activeSect === key
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-amber-100'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Grid */}
                    {filteredMantras.length === 0 ? (
                        <p className="text-center text-gray-500 py-10">No mantras match your filter.</p>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredMantras.map(m => (
                                <MantraCard
                                    key={m.uuid}
                                    mantra={m}
                                    expanded={expandedId === m.uuid}
                                    onToggle={() => setExpandedId(expandedId === m.uuid ? null : m.uuid)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Mantra detail modal */}
            {modalMantra && (
                <MantraModal
                    mantra={modalMantra}
                    onClose={() => setModalMantra(null)}
                />
            )}
        </div>
    );
};

export default Mantras;
